import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { getConfirm, submitConfirm, type ConfirmSummary, type ConfirmResult } from '../../lib/api'
import { balanceLabel } from '../../lib/balance'
import { m } from '../../paraglide/messages'

export const Route = createFileRoute('/confirm/$token')({
  component: ConfirmPage,
})

const fmtDT = new Intl.DateTimeFormat('he-IL', {
  weekday: 'long', day: '2-digit', month: '2-digit',
  hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem',
})

function durationLabel(mins: number): string {
  const h = Math.floor(mins / 60)
  const rem = mins % 60
  if (h && rem) return m.dur_h_m({ h, m: rem })
  if (h)        return h > 1 ? m.dur_hours({ count: h }) : m.dur_hour({ count: h })
  return m.duration_min({ count: rem })
}

function ConfirmPage() {
  const { token } = Route.useParams()
  const navigate  = useNavigate()

  const { data, isLoading, error } = useQuery<ConfirmSummary>({
    queryKey: ['confirm', token],
    queryFn:  () => getConfirm(token),
    retry:    false,
  })

  const [minutes, setMinutes] = useState(60)
  const [result, setResult]   = useState<ConfirmResult | null>(null)

  useEffect(() => { if (data) setMinutes(data.durationMin) }, [data])

  const confirm = useMutation({
    mutationFn: (happened: boolean) =>
      submitConfirm(token, { happened, actualDurationMin: minutes }),
    onSuccess: (r) => setResult(r),
  })

  if (isLoading) return <Centered><p className="text-zinc-400">{m.confirm_loading()}</p></Centered>
  if (error)     return <Centered><p className="text-red-600 text-sm">{(error as Error).message}</p></Centered>
  if (!data)     return null

  // ─── Result screen ──────────────────────────────────────────────────────────
  if (result) {
    return (
      <Centered>
        <div className="text-center flex flex-col items-center gap-4 w-full">
          <h1 className="text-lg font-semibold">{m.confirm_great()}</h1>
          <div className="w-full border border-zinc-200 divide-y divide-zinc-200 text-sm">
            <Row label={m.confirm_student()} value={result.studentName} />
            <Row label={m.confirm_balance()} value={balanceLabel(result.balance)} />
            <Row label={m.confirm_total_paid()} value={`₪${result.totalPaid.toFixed(2)}`} />
          </div>

          {result.remainingOpen > 0 && result.nextToken ? (
            <button
              onClick={() => {
                setResult(null)
                navigate({ to: '/confirm/$token', params: { token: result.nextToken! } })
              }}
              className="bg-zinc-900 text-white px-5 py-2 text-sm hover:bg-zinc-700 w-full"
            >
              {m.confirm_check_next({ count: result.remainingOpen })}
            </button>
          ) : (
            <p className="text-sm text-zinc-500">{m.confirm_all_caught_up()}</p>
          )}

          <Link
            to="/lessons"
            className="border border-zinc-900 px-5 py-2 text-sm hover:bg-zinc-100 w-full text-center"
          >
            {m.confirm_back()}
          </Link>
        </div>
      </Centered>
    )
  }

  // ─── Already confirmed ────────────────────────────────────────────────────────
  if (data.alreadyConfirmed) {
    return (
      <Centered>
        <div className="text-center flex flex-col items-center gap-3">
          <h1 className="text-base font-semibold">{data.studentName}</h1>
          <p className="text-sm text-zinc-500">{m.confirm_already()}</p>
          <Link
            to="/lessons"
            className="border border-zinc-900 px-5 py-2 text-sm hover:bg-zinc-100 w-full text-center mt-2"
          >
            {m.confirm_back()}
          </Link>
        </div>
      </Centered>
    )
  }

  // ─── Confirm screen ───────────────────────────────────────────────────────────
  return (
    <Centered>
      <div className="w-full flex flex-col gap-5">
        <div className="text-center">
          <h1 className="text-xl font-semibold">{data.studentName}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {data.scheduledAt ? fmtDT.format(new Date(data.scheduledAt)) : '—'}
          </p>
        </div>

        <div className="text-center">
          <p className="text-sm text-zinc-600 mb-3">
            {m.confirm_adjust({ duration: durationLabel(data.durationMin) })}
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => setMinutes(n => Math.max(15, n - 15))}
              className="w-12 h-12 border border-zinc-400 text-2xl leading-none hover:bg-zinc-100"
              aria-label={m.confirm_minutes_less()}
            >
              −
            </button>
            <div className="min-w-[7rem] text-center">
              <div className="text-2xl font-semibold tabular-nums">{durationLabel(minutes)}</div>
              <div className="text-xs text-zinc-400">{minutes} min</div>
            </div>
            <button
              type="button"
              onClick={() => setMinutes(n => n + 15)}
              className="w-12 h-12 border border-zinc-400 text-2xl leading-none hover:bg-zinc-100"
              aria-label={m.confirm_minutes_more()}
            >
              +
            </button>
          </div>
        </div>

        {confirm.error && (
          <p className="text-red-600 text-sm text-center">{(confirm.error as Error).message}</p>
        )}

        <button
          onClick={() => confirm.mutate(true)}
          disabled={confirm.isPending}
          className="bg-green-600 text-white px-6 py-3 text-base font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {confirm.isPending ? m.confirm_saving() : m.confirm_accept()}
        </button>

        <button
          onClick={() => confirm.mutate(false)}
          disabled={confirm.isPending}
          className="text-sm text-zinc-400 hover:text-red-600 disabled:opacity-40"
        >
          {m.confirm_didnt_happen()}
        </button>
      </div>
    </Centered>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="w-full max-w-sm">{children}</div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-3 py-2">
      <span className="text-zinc-500">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}
